import { useEffect, useState, type FormEvent } from "react";
import { useProcedure, useQuery } from "highscale/react";
import { PlayerIdentity } from "./PlayerIdentity";
import { at } from "./lexicons";
import { FutureRoundsEditor } from "./FutureRoundsEditor";
import { formatDeadline, timeLeft } from "./round-planning";
import { RoundPlanFields, useRoundPlan } from "./RoundPlanFields";
export function RoundWorkspace({
  league,
  isAdmin,
  testType,
  onCancelSetup,
  cancelling,
}: {
  league: string;
  isAdmin: boolean;
  testType: string;
  onCancelSetup?: () => void;
  cancelling?: boolean;
}) {
  const query = useQuery(at.tastetest.play.get, { league });
  const options = { invalidates: [at.tastetest.play.get] };
  const create = useProcedure(at.tastetest.play.create, options),
    submit = useProcedure(at.tastetest.play.submit, options),
    vote = useProcedure(at.tastetest.play.vote, options);
  const planner = useRoundPlan();
  const [url, setUrl] = useState(""),
    [title, setTitle] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [editing, setEditing] = useState(false);
  const [points, setPoints] = useState<Record<string, number>>({});
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
      void query.refetch();
    }, 15000);
    return () => clearInterval(timer);
  }, [query.refetch]);
  async function action(event: FormEvent, kind: "create" | "submit" | "vote") {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (kind === "create") {
        const plans = planner.plans();
        if (!plans.length || plans[0].submissionEndsAt <= Date.now())
          throw new Error("Choose a future first submission deadline.");
        const [first, ...plannedRounds] = plans;
        const result = await create({
          league,
          expectedRound: query.data?.roundNumber ?? 0,
          ...first,
          theme: first.theme.trim(),
          plannedRounds,
          upvotes: 6,
          openMembership: true,
        });
        if (!result.ok)
          throw new Error(result.message ?? "Could not schedule rounds.");
      } else if (kind === "submit")
        await submit({
          roundNumber: query.data?.roundNumber ?? 1,
          league,
          url: url.trim(),
          title: title.trim(),
        });
      else
        await vote({
          roundNumber: query.data?.roundNumber ?? 1,
          league,
          allocations: Object.entries(points)
            .filter(([, points]) => points > 0)
            .map(([token, points]) => ({ token, points })),
        });
    } catch (failure) {
      setError(
        kind === "create" &&
          failure instanceof Error &&
          [
            "Choose a future first submission deadline.",
            "Choose a start date and time in the future.",
          ].includes(failure.message)
          ? failure.message
          : "Could not save. Your entries are still here. Try again or refresh to check the round’s status.",
      );
    } finally {
      setBusy(false);
    }
  }
  const round = query.data;
  useEffect(() => {
    setPoints({});
    setUrl("");
    setTitle("");
    planner.reset();
  }, [round?.roundNumber, planner.reset]);
  return (
    <section className="round-workspace" aria-label="League round">
      {query.isPending && <p role="status">Loading round…</p>}
      {query.error && (
        <p role="alert">
          Could not load round.{" "}
          <button onClick={() => void query.refetch()}>Retry</button>
        </p>
      )}
      {round &&
        (!round.exists ||
          (round.phase === "results" &&
            !round.plannedRounds?.length &&
            (round.roundNumber ?? 1) < 20)) &&
        (isAdmin ? (
          <form onSubmit={(e) => void action(e, "create")}>
            <h3>Plan your rounds</h3>
            <RoundPlanFields
              plan={planner}
              testType={testType}
              busy={busy}
              offset={round.roundNumber ?? 0}
            />
            {error && (
              <p role="alert" className="error">
                {error}
              </p>
            )}
            <button disabled={busy}>
              {busy
                ? "Saving…"
                : `Save ${planner.drafts.length === 1 ? "round" : `${planner.drafts.length} rounds`}`}
            </button>
            {!round.exists && onCancelSetup && (
              <button
                type="button"
                className="secondary"
                disabled={busy || cancelling}
                onClick={onCancelSetup}
              >
                Cancel setup
              </button>
            )}
          </form>
        ) : (
          <p>The host hasn’t started a round yet.</p>
        ))}
      {round?.exists && (
        <>
          {isAdmin && (round.roundNumber ?? 1) < 20 && (
            <button
              type="button"
              className="secondary edit-league"
              onClick={() => setEditing(true)}
            >
              Edit league
            </button>
          )}
          {editing && (
            <FutureRoundsEditor
              league={league}
              roundNumber={round.roundNumber ?? 1}
              votingEndsAt={round.votingEndsAt!}
              plans={round.plannedRounds ?? []}
              testType={testType}
              onClose={() => setEditing(false)}
            />
          )}
          <header className="round-prompt">
            <span className="round-label">Round {round.roundNumber ?? 1}</span>
            <h3>
              {round.phase === "submitting"
                ? testType === "music"
                  ? "Submit a song"
                  : "Submit your best skeet"
                : round.phase === "voting"
                  ? "Vote for your favorites!"
                  : round.phase === "scheduled"
                    ? "Coming up next"
                    : "Round results"}
            </h3>
            <h4>{round.theme}</h4>
            {round.description && <p>{round.description}</p>}
            {(round.phase === "submitting" || round.phase === "voting") && (
              <div className="round-deadline">
                <strong>
                  {timeLeft(
                    round.phase === "submitting"
                      ? round.submissionEndsAt!
                      : round.votingEndsAt!,
                    now,
                  )}
                </strong>
                <span>
                  {round.phase === "submitting"
                    ? "Submissions due"
                    : "Votes due"}
                  :{" "}
                  {formatDeadline(
                    round.phase === "submitting"
                      ? round.submissionEndsAt!
                      : round.votingEndsAt!,
                  )}
                </span>
              </div>
            )}
          </header>
          {!!round.plannedRounds?.length && (
            <details>
              <summary>{round.plannedRounds.length} upcoming rounds</summary>
              <ol className="schedule-preview">
                {round.plannedRounds.map((plan, i) => (
                  <li key={i}>
                    <strong>
                      Round {(round.roundNumber ?? 1) + i + 1}: {plan.theme}
                    </strong>
                    <span>{plan.description}</span>
                    <span>
                      Submissions due: {formatDeadline(plan.submissionEndsAt)}
                    </span>
                    <span>Votes due: {formatDeadline(plan.votingEndsAt)}</span>
                  </li>
                ))}
              </ol>
            </details>
          )}
          {round.phase === "voting" && round.upvotes !== 6 && (
            <p>
              This older round uses the previous voting rules and cannot accept
              a new ranked ballot.
            </p>
          )}
          {!round.eligible && (
            <p>You can watch this round and participate in a future round.</p>
          )}
          {round.phase === "submitting" && round.eligible && (
            <form onSubmit={(e) => void action(e, "submit")}>
              <label>
                Pick title
                <input
                  required
                  maxLength={200}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label>
                {testType === "music"
                  ? "Music link (HTTPS)"
                  : "Bluesky post link"}
                <input
                  type="url"
                  required
                  maxLength={2000}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                />
              </label>
              <p>You can replace your pick until submissions close.</p>
              {error && (
                <p role="alert" className="error">
                  {error}
                </p>
              )}
              <button disabled={busy}>{busy ? "Saving…" : "Save pick"}</button>
            </form>
          )}
          {round.phase === "submitting" && round.picks.length > 0 && (
            <section className="saved-pick" aria-label="Your saved pick">
              <strong>Your pick is saved</strong>
              {round.picks.map((p) => (
                <a
                  key={p.token}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p.title}
                </a>
              ))}
            </section>
          )}
          {(round.phase === "voting" || round.phase === "results") && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (round.phase === "voting") void action(e, "vote");
              }}
            >
              <ul>
                {round.picks.map((p) => (
                  <li key={p.token}>
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      {p.title}
                    </a>
                    {p.provider && <span> · {p.provider}</span>}
                    {p.availability === "unavailable" && (
                      <p>
                        This post is unavailable or restricted. Its saved pick
                        and score remain.
                      </p>
                    )}
                    {p.availability === "changed" && (
                      <p>This post has changed since it was submitted.</p>
                    )}
                    {p.availability === "unknown" && (
                      <p>Could not check this post right now.</p>
                    )}
                    {p.isOwn && " · Your pick"}
                    {round.phase === "results" && (
                      <p>
                        #{p.rank} · {p.score} points ·{" "}
                        {p.owner && <PlayerIdentity did={p.owner} />}
                        {p.penalized ? " · Non-voter penalty" : ""}
                      </p>
                    )}
                    {round.phase === "voting" &&
                      round.eligible &&
                      round.upvotes === 6 &&
                      !p.isOwn && (
                        <label>
                          Points for {p.title}
                          <select
                            value={points[p.token] ?? 0}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              const next = { ...points };
                              if (value)
                                for (const token of Object.keys(next))
                                  if (next[token] === value) next[token] = 0;
                              setPoints({ ...next, [p.token]: value });
                            }}
                          >
                            <option value={0}>Unranked</option>
                            <option value={3}>1st choice · 3 points</option>
                            <option value={2}>2nd choice · 2 points</option>
                            <option value={1}>3rd choice · 1 point</option>
                          </select>
                        </label>
                      )}
                  </li>
                ))}
              </ul>
              {round.phase === "voting" &&
                round.eligible &&
                round.upvotes === 6 && (
                  <>
                    <p>
                      {round.picks.filter((p) => !p.isOwn).length < 3 &&
                        "There aren’t three eligible picks, so a complete ballot cannot be cast. "}
                      Choose three distinct picks: 3 points for your top choice,
                      2 for your second, and 1 for your third.{" "}
                      {round.hasVoted
                        ? "Your ballot is saved; saving again replaces it."
                        : "You haven’t voted yet."}
                    </p>
                    {error && (
                      <p role="alert" className="error">
                        {error}
                      </p>
                    )}
                    <button
                      disabled={
                        busy ||
                        Object.values(points)
                          .filter((n) => n > 0)
                          .sort((a, b) => b - a)
                          .join(",") !== "3,2,1"
                      }
                    >
                      Save ballot
                    </button>
                  </>
                )}
            </form>
          )}
        </>
      )}
      {!!round?.standings?.length && (
        <section aria-label="League standings">
          <h3>League standings</h3>
          <ol className="league-standings">
            {round.standings.map((row) => (
              <li key={row.owner}>
                <span>#{row.rank}</span>
                <PlayerIdentity did={row.owner} />
                <span>
                  {row.score} points · {row.roundsPlayed} rounds
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
      {!!round?.history?.length && (
        <section aria-label="Completed rounds">
          <h3>Round history</h3>
          {round.history.map((past) => (
            <details key={past.roundNumber}>
              <summary>
                Round {past.roundNumber}: {past.theme}
              </summary>
              {past.description && <p>{past.description}</p>}
              <ul>
                {past.picks.map((pick) => (
                  <li key={pick.token}>
                    <a
                      href={pick.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pick.title}
                    </a>{" "}
                    · #{pick.rank} · {pick.score} points ·{" "}
                    {pick.owner && <PlayerIdentity did={pick.owner} />}
                    {pick.penalized ? " · Non-voter penalty" : ""}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </section>
      )}
      {round?.roundNumber === 20 && round.phase === "results" && (
        <p>This league has completed the alpha limit of 20 rounds.</p>
      )}
    </section>
  );
}
